import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AppButton,
  AppCheckbox,
  AppInputNumber,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppSpin,
  AppTextField,
} from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { TENANT_ROUTES } from '@/constants/routes';
import {
  useTreatmentRecordPrintInit,
  useTreatmentRecordPrintOutput,
} from '@/hooks/main/useTreatmentRecordPrint';
import { useAppToast } from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import type { TreatmentRecordPrintPayload } from '@/pages/main/consent/services/print';

// -----------------------------------------------------------------------------
// Field groups — mirror RA2 list_template.blade.php (numbered ①-⑥ sections).
// Label semantic: "○○を印刷" — checked = print (RA2 UX).
// Backend inverts on save (DB stores 0=print / 1=hide to match RA2 schema).
// -----------------------------------------------------------------------------

const FRONT_CLINIC_ID = 'is_front_clinic_id';
const INSURANCE_TYPE = 'is_health_insurance_cards_type';

const MEDICAL_AID_CHILDREN = ['is_medical_aid_number', 'is_medical_aid_patient_number'];

const PATIENT_INFO_CHILDREN = [
  'is_health_insurance_card_mark',
  'is_health_insurance_card_number',
  'is_health_insurance_card_expiration_date',
  'is_health_insurance_card_qualification_date',
  'is_patient_information_name',
  'is_patient_information_name_kana',
  'is_patient_sex',
  'is_patient_information_relationship_id',
  'is_patient_information_date_of_birth',
  'is_office_name',
  'is_health_insurance_cards_insurer_number',
  'is_health_insurance_cards_insurer_name',
  'is_health_insurance_cards_name',
  'is_health_insurance_cards_name_kana',
  'is_health_insurance_card_sex',
  'is_health_insurance_cards_date_of_birth',
  'is_health_insurance_cards_address',
  'is_health_insurance_cards_address_kana',
];

const CONSENTS_INCIDENT_CHILDREN = [
  'is_consents_disease_name',
  'is_consents_incident_date',
  'is_consents_first_treatment_date',
  'is_first_visit_date',
  'is_last_visit_date',
  'is_consents_days',
  'is_consents_treatment_count',
  'is_rezepts_outcome_type_id',
];

const CONSENTS_HOSPITAL_CHILDREN_BASE = [
  'is_consents_hospital_name',
  'is_consents_hospital_address',
  'is_consents_hospital_telephone',
  'is_consents_doctor_name',
  'is_consents_doctor_name_kana',
  'is_consents_consent_date',
  'is_consents_incident_cause',
  'is_consents_incident_date_type_id',
  'is_pre_existing_illness',
];

const CONSENTS_HOSPITAL_CHILDREN = [...CONSENTS_HOSPITAL_CHILDREN_BASE, 'is_length'];
const CONSENTS_HOSPITAL_CHILDREN_R2 = [...CONSENTS_HOSPITAL_CHILDREN_BASE, 'is_consents_symptom'];

const CONSENTS_TYPE_CHILDREN = [
  'is_consents_massage',
  'is_consents_orthomorphia',
  'is_consents_hot_treatment',
  'is_length',
];

const FRONT_ALL = Array.from(
  new Set([
    FRONT_CLINIC_ID,
    INSURANCE_TYPE,
    ...MEDICAL_AID_CHILDREN,
    ...PATIENT_INFO_CHILDREN,
    ...CONSENTS_INCIDENT_CHILDREN,
    ...CONSENTS_HOSPITAL_CHILDREN,
    ...CONSENTS_HOSPITAL_CHILDREN_R2,
    ...CONSENTS_TYPE_CHILDREN,
  ])
);

const BACK_ALL = [
  'is_back_clinic_id',
  'is_day_check',
  'is_massage_price',
  'is_orthomorphia_price',
  'is_visit_clinic_price',
  'is_visit_treatment_price',
  'is_hot_fomentation_price',
  'is_special_area_price',
  'is_transportation_price',
  'is_subtotal_price',
  'is_part_of_charge_day',
  'is_visits_massager_remarks',
  'is_remarks',
  'is_treatment_yearmonth',
  'is_billing_yearmonth',
  'is_total_price',
  'is_part_of_charge',
  'is_amount_claimed',
  'is_visits_count',
];

const ALL_FLAG_NAMES = [...FRONT_ALL, ...BACK_ALL];

// -----------------------------------------------------------------------------
// Preview overlay groups
// -----------------------------------------------------------------------------

const RANGE_GROUPS_A2: { src: string; flags: string[] }[] = [
  { src: '/images/treatment-record/print_range1.png', flags: [FRONT_CLINIC_ID] },
  { src: '/images/treatment-record/print_range2.png', flags: [INSURANCE_TYPE] },
  { src: '/images/treatment-record/print_range3.png', flags: MEDICAL_AID_CHILDREN },
  { src: '/images/treatment-record/print_range4.png', flags: PATIENT_INFO_CHILDREN },
  { src: '/images/treatment-record/print_range5.png', flags: CONSENTS_INCIDENT_CHILDREN },
  { src: '/images/treatment-record/print_range6.png', flags: CONSENTS_HOSPITAL_CHILDREN },
];

const RANGE_GROUPS_R2: { src: string; flags: string[] }[] = [
  { src: '/images/treatment-record/print_range1.png', flags: [FRONT_CLINIC_ID] },
  { src: '/images/treatment-record/print_range2.png', flags: [INSURANCE_TYPE] },
  { src: '/images/treatment-record/print_range3.png', flags: MEDICAL_AID_CHILDREN },
  { src: '/images/treatment-record/print_range4.png', flags: PATIENT_INFO_CHILDREN },
  { src: '/images/treatment-record/print_range5.png', flags: CONSENTS_INCIDENT_CHILDREN },
  { src: '/images/treatment-record/print_range6_r2.png', flags: CONSENTS_HOSPITAL_CHILDREN_BASE },
  { src: '/images/treatment-record/print_range7.png', flags: CONSENTS_TYPE_CHILDREN },
];

// Overlay shown when AT LEAST ONE field in the group is checked (will be printed).
const isRangeVisible = (flags: Record<string, boolean>, fields: string[]): boolean =>
  fields.some(f => !!flags[f]);

const anyChecked = (flags: Record<string, boolean>, fields: string[]): boolean =>
  fields.some(f => !!flags[f]);
// -----------------------------------------------------------------------------

export const TreatmentRecordConsentPrintPage: React.FC = () => {
  const { tenant, patientId, consentId } = useParams();
  const navigate = useNavigate();
  const toast = useAppToast();
  const sidebarOpen = useSidebarOpen();
  const { serviceType } = useTheme();
  const isR2 = serviceType === SERVICE_TYPE_R2;
  const consentsHospitalChildren = isR2
    ? CONSENTS_HOSPITAL_CHILDREN_R2
    : CONSENTS_HOSPITAL_CHILDREN;

  const patientIdNum = Number(patientId);
  const consentIdNum = Number(consentId);

  const {
    data: initData,
    isLoading,
    isError,
  } = useTreatmentRecordPrintInit(patientIdNum, consentIdNum, !!patientIdNum && !!consentIdNum);
  const outputMutation = useTreatmentRecordPrintOutput(patientIdNum, consentIdNum);

  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [officeName, setOfficeName] = useState('');
  const [doctorNameKana, setDoctorNameKana] = useState('');
  const [length, setLength] = useState('');
  const [outputRadio, setOutputRadio] = useState<1 | 2 | 3>(1);
  /** 期間指定: YYYYMM ints selected from visit-months dropdown. */
  const [searchFrom, setSearchFrom] = useState<number | null>(null);
  const [searchTo, setSearchTo] = useState<number | null>(null);

  const visitYearMonths = useMemo(
    () => (initData?.visit_year_months ?? []).map(v => Number(v)).filter(v => Number.isFinite(v)),
    [initData]
  );
  const hasVisitMonths = visitYearMonths.length > 0;

  useEffect(() => {
    if (!initData) return;
    const merged: Record<string, boolean> = {};
    ALL_FLAG_NAMES.forEach(name => {
      merged[name] = !!initData.flags?.[name];
    });
    // 裏面 flags: always default ON regardless of DB state (per spec).
    BACK_ALL.forEach(name => {
      merged[name] = true;
    });
    setFlags(merged);
    setOfficeName(initData.office_name ?? '');
    setDoctorNameKana(initData.consents_doctor_name_kana ?? '');
    setLength(initData.length ?? '');
    // 期間指定 default: FROM = earliest visit month, TO = latest visit month.
    const yms = (initData.visit_year_months ?? [])
      .map(v => Number(v))
      .filter(v => Number.isFinite(v));
    setSearchFrom(yms.length ? yms[0] : null);
    setSearchTo(yms.length ? yms[yms.length - 1] : null);
  }, [initData]);

  /** Format YYYYMM int → "YYYY年MM月" label. */
  const ymToLabel = (ym: number): string => {
    const s = String(ym);
    return `${s.slice(0, 4)}年${s.slice(4, 6)}月`;
  };

  const fromOptions = useMemo(
    () =>
      visitYearMonths
        .filter(ym => searchTo == null || ym <= searchTo)
        .map(ym => ({ value: ym, label: ymToLabel(ym) })),
    [visitYearMonths, searchTo]
  );
  const toOptions = useMemo(
    () =>
      visitYearMonths
        .filter(ym => searchFrom == null || ym >= searchFrom)
        .map(ym => ({ value: ym, label: ymToLabel(ym) })),
    [visitYearMonths, searchFrom]
  );

  const editPath = useMemo(
    () => (tenant ? TENANT_ROUTES.CONSENTS.EDIT(tenant, patientId ?? '', consentId ?? '') : '/'),
    [tenant, patientId, consentId]
  );

  const setFlag = (name: string, value: boolean) => setFlags(prev => ({ ...prev, [name]: value }));

  const setFlagsBulk = (names: string[], value: boolean) =>
    setFlags(prev => {
      const next = { ...prev };
      names.forEach(n => {
        next[n] = value;
      });
      return next;
    });

  const handleBack = () => navigate(editPath);

  const handleSubmit = async () => {
    if (!initData) return;

    if (!hasVisitMonths) {
      toast.error('対象データがありません。');
      return;
    }

    const payload: TreatmentRecordPrintPayload = {
      rezept_ids: initData.rezept_ids ?? [],
      output_radio: outputRadio,
      search_treatment_from: searchFrom,
      search_treatment_to: searchTo,
      office_name: officeName,
      consents_doctor_name_kana: doctorNameKana,
      length,
      ...flags,
    };

    try {
      const preview = await outputMutation.mutateAsync(payload);
      // 表面 is merged into a single page here, so when the scope holds more than one
      // 保険証 the user is told the latest one wins before the tab opens (spec §4.3).
      // On cancel the PDF is not opened and the user stays on the settings screen.
      if (preview.multi_hic_warning && !window.confirm(preview.multi_hic_warning)) {
        return;
      }
      window.open(preview.url, '_blank', 'noopener,noreferrer');
      navigate(editPath);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'PDF出力に失敗しました。'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  if (isError || !initData) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-10">
        <p className="text-base text-gray-700">データが取得できません。</p>
        <AppButton type="link" onClick={handleBack}>
          戻る
        </AppButton>
      </div>
    );
  }

  // -------- Front preview overlay --------
  const renderFrontPreview = () => (
    <div className="relative w-full max-w-[530px] mx-auto">
      <img
        src={
          isR2
            ? '/images/treatment-record/print_front_base_r2.png'
            : '/images/treatment-record/print_front_base.png'
        }
        alt="施術録 表面"
        className="block w-full"
      />
      {(isR2 ? RANGE_GROUPS_R2 : RANGE_GROUPS_A2).map((g, idx) => (
        <img
          key={idx}
          src={g.src}
          alt=""
          aria-hidden="true"
          className="absolute top-0 left-0 w-full pointer-events-none"
          style={{ display: isRangeVisible(flags, g.flags) ? 'block' : 'none' }}
        />
      ))}
    </div>
  );

  // -------- Numbered section header --------
  const numberMark = (n: string) => (
    <span className="inline-block w-6 text-base font-bold shrink-0">{n}</span>
  );

  const groupHeader = (num: string, title: string, children: string[]) => (
    <div className="flex items-center gap-2 mt-2">
      {numberMark(num)}
      <AppCheckbox
        checked={anyChecked(flags, children)}
        onChange={
          ((e: { target: { checked: boolean } }) =>
            setFlagsBulk(children, e.target.checked)) as never
        }
      >
        {title}　を印刷
      </AppCheckbox>
    </div>
  );

  const singleField = (num: string, field: string, label: string) => (
    <div className="flex items-center gap-2 mt-2">
      {numberMark(num)}
      <AppCheckbox
        checked={!!flags[field]}
        onChange={
          ((e: { target: { checked: boolean } }) => setFlag(field, e.target.checked)) as never
        }
      >
        {label}　を印刷
      </AppCheckbox>
    </div>
  );

  const childCheckbox = (field: string, label: string) => (
    <AppCheckbox
      key={field}
      checked={!!flags[field]}
      onChange={
        ((e: { target: { checked: boolean } }) => setFlag(field, e.target.checked)) as never
      }
    >
      {label}
    </AppCheckbox>
  );

  const subRow = (children: React.ReactNode) => (
    <div className="flex flex-wrap mt-1 ml-12 gap-x-4 gap-y-1">{children}</div>
  );

  // -------- Sections --------
  return (
    <div className="p-4 text-sm patient-edit-page">
      <div className="grid grid-cols-1 lg:grid-cols-[530px_1fr] gap-6">
        {/* LEFT — preview */}
        <div className="p-3 border rounded">
          <div className="mb-2 font-semibold">■施術録</div>
          {renderFrontPreview()}
        </div>

        {/* RIGHT — checkboxes */}
        <div className="p-3 border rounded">
          <div className="mb-2 text-red-600">※表面裏面全てOFFの場合は雛形印刷されます</div>

          {/* ----- 表面 ----- */}
          <div className="flex items-center gap-2 mb-1 font-semibold">
            ■表面　全印刷
            <AppButton size="small" onClick={() => setFlagsBulk(FRONT_ALL, true)}>
              ON
            </AppButton>
            <AppButton size="small" onClick={() => setFlagsBulk(FRONT_ALL, false)}>
              OFF
            </AppButton>
          </div>

          {singleField('①', FRONT_CLINIC_ID, '治療院ID-受診者No')}
          {singleField('②', INSURANCE_TYPE, '保険種別')}

          {groupHeader('③', '公費負担医療', MEDICAL_AID_CHILDREN)}
          {subRow([
            childCheckbox('is_medical_aid_number', '公費負担者番号'),
            childCheckbox('is_medical_aid_patient_number', '公費受給者番号'),
          ])}

          {groupHeader('④', '保険証面', PATIENT_INFO_CHILDREN)}
          <div className="mt-1 ml-12">（施術を受ける者）</div>
          {subRow([
            childCheckbox('is_health_insurance_card_mark', '記号'),
            childCheckbox('is_health_insurance_card_number', '番号'),
            childCheckbox('is_health_insurance_card_expiration_date', '有効期限'),
            childCheckbox('is_health_insurance_card_qualification_date', '資格取得年月日'),
          ])}
          {subRow([
            childCheckbox('is_patient_information_name', '氏名'),
            childCheckbox('is_patient_information_name_kana', '氏名（フリガナ）'),
            childCheckbox('is_patient_sex', '性別'),
            childCheckbox('is_patient_information_relationship_id', '続柄'),
            childCheckbox('is_patient_information_date_of_birth', '生年月日'),
          ])}
          <div className="flex items-center gap-2 mt-1 ml-12">
            {childCheckbox('is_office_name', '事業所')}
            <AppTextField
              value={officeName}
              onChange={e => setOfficeName(e.target.value)}
              className="w-60"
              size="small"
            />
          </div>
          {subRow([
            childCheckbox('is_health_insurance_cards_insurer_number', '番号'),
            childCheckbox('is_health_insurance_cards_insurer_name', '名称'),
          ])}
          <div className="mt-1 ml-12">（被保険者）</div>
          {subRow([
            childCheckbox('is_health_insurance_cards_name', '氏名'),
            childCheckbox('is_health_insurance_cards_name_kana', '氏名（フリガナ）'),
            childCheckbox('is_health_insurance_card_sex', '性別'),
            childCheckbox('is_health_insurance_cards_date_of_birth', '生年月日'),
            childCheckbox('is_health_insurance_cards_address', '住所'),
            childCheckbox('is_health_insurance_cards_address_kana', '住所（フリガナ）'),
          ])}

          {groupHeader('⑤', '病名～転記', CONSENTS_INCIDENT_CHILDREN)}
          {subRow([
            childCheckbox('is_consents_disease_name', '病名'),
            childCheckbox('is_consents_incident_date', '発症年月日'),
            childCheckbox('is_consents_first_treatment_date', '初療年月日'),
            childCheckbox('is_first_visit_date', '施術開始日'),
            childCheckbox('is_last_visit_date', '施術終了日'),
          ])}
          {subRow([
            childCheckbox('is_consents_days', '日数'),
            childCheckbox('is_consents_treatment_count', '施術回数'),
            childCheckbox('is_rezepts_outcome_type_id', '転帰'),
          ])}

          {groupHeader('⑥', '同意記録', consentsHospitalChildren)}
          {subRow([
            childCheckbox('is_consents_hospital_name', '病院名'),
            childCheckbox('is_consents_hospital_address', '住所'),
            childCheckbox('is_consents_hospital_telephone', '電話'),
            childCheckbox('is_consents_doctor_name', '同意医師名'),
          ])}
          <div className="flex flex-wrap items-center gap-2 mt-1 ml-12">
            {childCheckbox('is_consents_doctor_name_kana', '同意医師名（フリガナ）')}
            <AppTextField
              value={doctorNameKana}
              onChange={e => setDoctorNameKana(e.target.value)}
              className="w-60"
              size="small"
            />
          </div>
          {subRow([
            childCheckbox('is_consents_consent_date', '同意'),
            childCheckbox('is_consents_incident_cause', '発症の原因'),
            childCheckbox('is_consents_incident_date_type_id', '第三者行為'),
            ...(isR2 ? [childCheckbox('is_consents_symptom', '同意症状')] : []),
          ])}
          <div className="flex flex-wrap items-center gap-2 mt-1 ml-12">
            {childCheckbox('is_pre_existing_illness', '既往症・主要症状・経過等')}
            {!isR2 && (
              <>
                {childCheckbox('is_length', '往療距離')}
                <AppInputNumber
                  value={length === '' ? null : Number(length)}
                  onChange={v => setLength(v == null ? '' : String(v))}
                  min={0}
                  className="w-32"
                  size="small"
                />
              </>
            )}
          </div>

          {isR2 && (
            <>
              {groupHeader('⑦', '施術の種類', CONSENTS_TYPE_CHILDREN)}
              <div className="flex flex-wrap items-center gap-2 mt-1 ml-12">
                {childCheckbox('is_consents_massage', 'マッサージ')}
                {childCheckbox('is_consents_orthomorphia', '変形徒手矯正術')}
                {childCheckbox('is_consents_hot_treatment', '温罨法')}
                {childCheckbox('is_length', '往療距離')}
                <AppInputNumber
                  value={length === '' ? null : Number(length)}
                  onChange={v => setLength(v == null ? '' : String(v))}
                  min={0}
                  className="w-32"
                  size="small"
                />
              </div>
            </>
          )}

          <hr className="my-4" />

          {/* ----- 裏面 ----- */}
          <div className="flex items-center gap-2 mb-2 font-semibold">
            ■裏面　申請書印刷済　全印刷
            <AppButton size="small" onClick={() => setFlagsBulk(BACK_ALL, true)}>
              ON
            </AppButton>
            <AppButton size="small" onClick={() => setFlagsBulk(BACK_ALL, false)}>
              OFF
            </AppButton>
          </div>

          <div className="ml-12">
            <div>{childCheckbox('is_back_clinic_id', '治療院ID-受診者No、受診者名')}</div>
            <div className="grid grid-cols-4 mt-1 gap-x-4 gap-y-1">
              {childCheckbox('is_day_check', '日付')}
              {isR2
                ? childCheckbox('is_massage_price', '通所')
                : childCheckbox('is_visit_clinic_price', '通所')}
              {isR2 && childCheckbox('is_orthomorphia_price', '変形徒手矯正術部位')}
              {childCheckbox(
                'is_hot_fomentation_price',
                isR2 ? '温罨法又は温罨法・電療' : '温(鍼・温)'
              )}
              {childCheckbox('is_visit_treatment_price', '訪問施術料')}
              {childCheckbox('is_special_area_price', '特別地域加算')}
              {childCheckbox('is_transportation_price', '往療料')}
              {childCheckbox('is_subtotal_price', '合計金額')}
              {childCheckbox('is_part_of_charge_day', '一部負担金')}
              {childCheckbox('is_visits_massager_remarks', '施術経過所見')}
              {childCheckbox('is_remarks', '備考')}
              {childCheckbox('is_treatment_yearmonth', '施術年月')}
              {childCheckbox('is_billing_yearmonth', '請求年月')}
              {childCheckbox('is_total_price', '合計金額')}
              {childCheckbox('is_part_of_charge', '一部負担金')}
              {childCheckbox('is_amount_claimed', '請求金額')}
              {childCheckbox('is_visits_count', '回数')}
            </div>
          </div>

          {/* 期間指定: narrow print range to months that actually have visits. */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="font-semibold">期間指定</span>
            <AppSelect
              value={searchFrom ?? undefined}
              onChange={v => setSearchFrom(v == null ? null : Number(v))}
              options={fromOptions}
              placeholder="年月指定"
              disabled={!hasVisitMonths}
              allowClear={false}
              className="w-[200px]"
            />
            <span>～</span>
            <AppSelect
              value={searchTo ?? undefined}
              onChange={v => setSearchTo(v == null ? null : Number(v))}
              options={toOptions}
              placeholder="年月指定"
              disabled={!hasVisitMonths}
              allowClear={false}
              className="w-[200px]"
            />
          </div>
        </div>
      </div>

      {/* Pushes content above the fixed BottomBar so it isn't clipped. */}
      <div className="h-20" />

      <BottomBar
        variant="action"
        visible
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <AppButton
            type="default"
            className="rounded"
            onClick={handleBack}
            disabled={outputMutation.isPending}
          >
            戻る
          </AppButton>
        }
        right={
          <div className="flex items-center gap-4">
            <AppRadioGroup
              value={outputRadio}
              onChange={e => setOutputRadio(e.target.value as 1 | 2 | 3)}
            >
              <AppRadio value={1} className="!text-white">
                表面
              </AppRadio>
              <AppRadio value={2} className="!text-white">
                裏面
              </AppRadio>
              <AppRadio value={3} className="!text-white">
                両面
              </AppRadio>
            </AppRadioGroup>
            <AppButton type="primary" onClick={handleSubmit} loading={outputMutation.isPending}>
              印刷
            </AppButton>
          </div>
        }
      />
    </div>
  );
};

export default TreatmentRecordConsentPrintPage;
